import type { UsageSheetAppData } from '@shared/types/forge-usage-sheet';
import { APP_OBJECT_KIND_LABELS, APP_OBJECT_KINDS, type AppObjectKind } from '@shared/types/app-objects';
import { EditableText } from './EditableText';
import { SheetRow, SheetSection } from './SheetLines';

export const AppDataSection = ({ data, editable, onChange }: {
  data: UsageSheetAppData; editable: boolean; onChange: (data: UsageSheetAppData) => void;
}) => {
  const text = (value: string, label: string, onSave: (value: string) => void) =>
    <EditableText value={value} label={label} editable={editable} onSave={onSave} maxLength={160} />;
  return <SheetSection title="Données de l’app">
    <p className="text-sm text-domino-ink">Liste : {text(data.title, 'Nom de la liste', (title) => onChange({ ...data, title }))}</p>
    <label className="block text-sm text-domino-ink">
      À chaque lancement
      <select aria-label="À chaque lancement" disabled={!editable} value={data.mode}
        className="mt-1 block w-full rounded-md border border-domino-cardBorder bg-domino-white px-2 py-1.5"
        onChange={(event) => {
          const mode = event.target.value as UsageSheetAppData['mode'];
          onChange({ ...data, mode, object: mode === 'rows' ? data.object ?? 'company' : null });
        }}>
        <option value="rows">Retrouver les mêmes objets</option>
        <option value="runs">Ajouter un résultat distinct</option>
      </select>
    </label>
    {data.mode === 'rows' && <label className="block text-sm text-domino-ink">
      Chaque objet est
      <select aria-label="Chaque objet est" disabled={!editable} value={data.object ?? 'other'}
        className="mt-1 block w-full rounded-md border border-domino-cardBorder bg-domino-white px-2 py-1.5"
        onChange={(event) => onChange({ ...data, object: event.target.value as AppObjectKind })}>
        {APP_OBJECT_KINDS.map((kind) => <option key={kind} value={kind}>{APP_OBJECT_KIND_LABELS[kind]}</option>)}
      </select>
    </label>}
    <p className="text-sm text-domino-inkSoft">{data.mode === 'rows'
      ? 'Un objet déjà présent est actualisé. Ses statuts, notes et commentaires sont conservés.'
      : 'Chaque lancement ajoute une fiche avec son résultat et le suivi de l’équipe.'}</p>
    <p className="text-sm font-medium text-domino-ink">Statuts de l’équipe</p>
    <p className="text-sm text-domino-inkSoft">Le premier statut s’applique aux nouveaux objets. Cochez les statuts qui terminent le suivi.</p>
    {data.statuses.map((status, index) => <SheetRow key={status.key} showWhy={false}
      removeLabel={`Retirer le statut ${status.label}`}
      onRemove={editable && data.statuses.length > 1 ? () => onChange({ ...data, statuses: data.statuses.filter((_, i) => i !== index) }) : undefined}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {text(status.label, `Statut ${status.label}`, (label) => onChange({ ...data, statuses: data.statuses.map((s, i) => i === index ? { ...s, label } : s) }))}
        <label className="flex items-center gap-1.5 text-sm text-domino-inkSoft">
          <input type="checkbox" disabled={!editable} checked={status.done} aria-label={`Suivi terminé : ${status.label}`}
            onChange={(event) => onChange({ ...data, statuses: data.statuses.map((s, i) => i === index ? { ...s, done: event.target.checked } : s) })} />
          Suivi terminé
        </label>
      </div>
    </SheetRow>)}
    {editable && <button type="button" className="text-sm text-domino-aubergine underline underline-offset-4"
      onClick={() => {
        let number = 1;
        while (data.statuses.some((status) => status.key === `status_${number}`)) number++;
        onChange({ ...data, statuses: [...data.statuses, { key: `status_${number}`, label: 'Nouveau statut', done: false }] });
      }}>Ajouter un statut</button>}
  </SheetSection>;
};
